import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuthStore } from '@/shared/stores/auth.store';
import { ROLE_PERMISSIONS } from '@/shared/permissions/permissions';
import type { CurrentUser } from '@/shared/types';
import type { Role } from '@/shared/permissions/roles';
import type { Permission } from '@/shared/permissions/permissions';

interface RoleContextValue {
  user: CurrentUser | null;
  role: Role | null;
  department: string | null;
  permissions: Set<Permission>;
  /** UI gating only — server stays authoritative. */
  can: (permission: Permission) => boolean;
  hasRole: (...roles: Role[]) => boolean;
}

const RoleContext = createContext<RoleContextValue | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);

  const value = useMemo<RoleContextValue>(() => {
    const permissions = new Set<Permission>(user?.role ? ROLE_PERMISSIONS[user.role as Role] || [] : []);
    return {
      user,
      role: user?.role ?? null,
      department: user?.department ?? null,
      permissions,
      can: (permission) => permissions.has(permission),
      hasRole: (...roles) => !!user && roles.includes(user.role),
    };
  }, [user]);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within <RoleProvider>.');
  return ctx;
}
